import { useEffect, useMemo, useState } from "react";
import AceEditor from "react-ace";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { executeQuery } from "./index";
import Alert, { setAlert, selectAlert } from "../../components/organisms/Alert";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import {
  ALERT_TIMEOUT,
  DATA_TYPES,
  DEFAULT_INT_VALUES,
  EMPTY_ARRAY,
  EMPTY_STRING,
} from "../../constants";

const { EXECUTE_QUERY } = SPINNER_NAMES;
const { ZERO } = DEFAULT_INT_VALUES;
const RECORDS_PER_PAGE = 12;
const { NUMBER, STRING } = DATA_TYPES;

const QueryToolModel = ({ title, name, values, helperText = EMPTY_STRING }) => {
  const dispatch = useDispatch();
  const dataModelId = useParams();
  const selectedAlert = useSelector(selectAlert);

  const [inputData, setInputData] = useState();
  const [alertData, setAlertData] = useState();
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [rowData, setRowData] = useState(EMPTY_ARRAY);
  const [columnDefs, setColumnDefs] = useState(EMPTY_ARRAY);
  const [integerRes, setIntegerRes] = useState();
  const [strRes, setStrRes] = useState();
  const [exceptionEncountered, setExceptionEncountered] = useState(false);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 100,
    }),
    []
  );

  const handleOnClear = () => setInputData(EMPTY_STRING);

  const handleOnExecute = () => {
    const dataModelIdValue = dataModelId.dataModelId;
    const data = { expression: `${inputData}`, dataModelIdValue };

    dispatch(executeQuery(data)).then(({ payload }) => {
      setStrRes();
      setIntegerRes();
      setRowData(EMPTY_ARRAY);
      if (!payload) setExceptionEncountered(true);
      else
        switch (typeof payload) {
          case STRING: {
            setStrRes(payload);
            break;
          }
          case NUMBER: {
            setIntegerRes(payload);
            break;
          }
          default: {
            const columnDefs = Object.keys(payload[ZERO]).map((key) => ({
              field: key,
            }));
            setColumnDefs(columnDefs);
            setRowData(payload);
            setTimeout(() => dispatch(setAlert()), ALERT_TIMEOUT);
          }
        }
    });
  };

  useEffect(() => {
    values && setInputData(values[name]);
  }, [name, values]);

  useEffect(() => {
    if (exceptionEncountered && selectedAlert) {
      const errorMessage = `Error:    ${selectedAlert.message}`;
      setStrRes(errorMessage);
      setExceptionEncountered(false);
    }
  }, [selectedAlert, exceptionEncountered]);

  return (
    <div className="content scrollbar overflow-auto p-6 flex-1">
      <div className="bg-gray-800 border border-gray-700 rounded w-full mx-auto my-6 sm:w-auto">
        <div className="flex items-center p-6 border-b border-gray-700">
          <div>
            <h6 className="text-base text-gray-50 font-bold">{title}</h6>
          </div>
        </div>
        {alertData && (
          <div className="m-2">
            <Alert
              type={alertData.type}
              message={alertData.message}
              handleOnClose={setAlertData}
            />
          </div>
        )}
        <div className="border-b-2 border-gray-700 text-white">
          <AceEditor
            mode="sql"
            theme="twilight"
            name="aceEditor"
            height="150px"
            width="100%"
            value={inputData}
            onChange={setInputData}
            setOptions={{
              showGutter: true,
              enableLiveAutocompletion: true,
              fontSize: 14,
              highlightActiveLine: true,
              tabSize: 4,
              useWorker: false,
              showPrintMargin: false,
            }}
          />
        </div>
        {helperText && (
          <div className="flex text-xs mx-4 mt-2">
            <p className="mr-2 font-bold">Note:</p>
            <p className="text-red-600 whitespace-pre-line">{helperText}</p>
          </div>
        )}
        <div className="p-6 border-t border-gray-700 flex align-center justify-end">
          <button
            className="btn btn-primary-light mr-1"
            type="button"
            onClick={handleOnClear}
          >
            Clear
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            onClick={handleOnExecute}
            disabled={!inputData || isSpinnerActive}
          >
            <Spinner
              name={EXECUTE_QUERY}
              setIsSpinnerActive={setIsSpinnerActive}
            >
              Execute
            </Spinner>
          </button>
        </div>
      </div>
      {!integerRes && !strRes && !!rowData.length && (
        <div>
          <AgGridReact
            deltaRowDataMode
            rowData={rowData}
            domLayout={"autoHeight"}
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            className="ag-theme-alpine"
            animateRows="true"
            pagination={true}
            rowSelection={"multiple"}
            rowGroupPanelShow={"always"}
            pivotPanelShow={"always"}
            suppressRowClickSelection={true}
            groupSelectsChildren={true}
            paginationPageSize={RECORDS_PER_PAGE}
          />
        </div>
      )}
      {integerRes && (
        <div className="border-b-2 border-gray-700 text-white">
          Rows affected = {integerRes}
        </div>
      )}
      {strRes && (
        <div className="border-b-2 border-gray-700 text-white whitespace-pre">
          {strRes}
        </div>
      )}
    </div>
  );
};

export default QueryToolModel;
